import { createElement } from "react";
import { createRoot } from "react-dom/client";

import { VNextToast } from "@/vnext/primitives/VNextToast";

export interface AdminSignOutLock {
  release(): void;
  recover(): void;
}

let activeLock: AdminSignOutLock | null = null;
let dismissFailureNotice: (() => void) | null = null;

/** Logout can originate outside the shell's toast provider. Own this one body root. */
export function showAdminSignOutFailure(
  reason: "lock" | "redirect" = "lock",
): void {
  dismissFailureNotice?.();
  const body = document.querySelector("body");
  if (!body) return;
  const host = document.createElement("div");
  body.append(host);
  const root = createRoot(host);
  const dismiss = () => {
    if (dismissFailureNotice !== dismiss) return;
    dismissFailureNotice = null;
    root.unmount();
    host.remove();
  };
  dismissFailureNotice = dismiss;
  root.render(
    createElement(VNextToast, {
      notification: {
        id: "admin-sign-out-failed",
        tone: "error",
        message:
          reason === "lock"
            ? "We could not start signing out. Please try again."
            : "We could not open the sign-in page. Reload the page to continue.",
      },
      onDismiss: dismiss,
    }),
  );
}

export function isAdminSignOutLocked(): boolean {
  return activeLock !== null;
}

const shieldedEvents = [
  "beforeinput",
  "input",
  "change",
  "submit",
  "click",
  "dblclick",
  "pointerdown",
  "pointerup",
  "pointermove",
  "pointercancel",
  "mousedown",
  "mouseup",
  "mousemove",
  "touchstart",
  "touchend",
  "touchmove",
  "keydown",
  "keypress",
  "keyup",
  "paste",
  "cut",
  "dragstart",
  "drop",
  "contextmenu",
] as const;

/** A native top-layer lock outside React, retained through auth/route remounts. */
export function acquireAdminSignOutLock(
  reload: () => void = () => window.location.reload(),
): AdminSignOutLock | null {
  const body = document.querySelector("body");
  if (activeLock !== null || !body) return null;
  dismissFailureNotice?.();
  const dialog = document.createElement("dialog");
  // Do not silently replace native modality with a visual-only overlay.
  if (typeof dialog.showModal !== "function") return null;
  const opener = document.activeElement;
  const title = document.createElement("h2");
  title.id = "admin-sign-out-lock-title";
  title.textContent = "Signing out";
  title.tabIndex = -1;
  title.setAttribute("autofocus", "");
  const status = document.createElement("p");
  status.id = "admin-sign-out-lock-status";
  status.textContent = "Please wait. Your session is being closed.";
  status.setAttribute("role", "status");
  dialog.setAttribute("aria-labelledby", title.id);
  dialog.setAttribute("aria-describedby", status.id);
  dialog.setAttribute("aria-busy", "true");
  dialog.setAttribute("closedby", "none");
  dialog.append(title, status);

  let reloadButton: HTMLButtonElement | null = null;
  const focusLock = () =>
    (reloadButton ?? title).focus({ preventScroll: true });
  const shield = (event: Event) => {
    const inside =
      event.target instanceof Node && dialog.contains(event.target);
    if (inside && !event.type.startsWith("key")) return;
    // Keep native Tab behavior in the modal, but shell/window shortcuts and
    // events aimed at the background cannot edit or navigate the signed-out page.
    const activatesReload =
      event instanceof KeyboardEvent &&
      event.target === reloadButton &&
      (event.key === "Enter" || event.key === " ") &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey;
    if (
      !inside ||
      (event instanceof KeyboardEvent &&
        event.key !== "Tab" &&
        !activatesReload)
    ) {
      event.preventDefault();
    }
    event.stopImmediatePropagation();
  };
  const keepFocus = (event: FocusEvent) => {
    if (event.target instanceof Node && !dialog.contains(event.target)) {
      event.stopImmediatePropagation();
      focusLock();
    }
  };
  const cancel = (event: Event) => event.preventDefault();
  const beforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = "";
  };
  let released = false;
  const lock: AdminSignOutLock = {
    recover() {
      if (released) return;
      if (reloadButton) {
        focusLock();
        return;
      }
      window.removeEventListener("beforeunload", beforeUnload);
      title.textContent = "Reload to continue";
      status.textContent =
        "We could not open the sign-in page. Reload this page to continue.";
      dialog.removeAttribute("aria-busy");
      dialog.setAttribute("role", "alertdialog");
      // Obscure stale page content after local security cleanup, including when
      // an auth-gate navigation was blocked while provider sign-out was pending.
      Object.assign(dialog.style, {
        position: "fixed",
        inset: "0",
        margin: "0",
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
        maxWidth: "none",
        maxHeight: "none",
        border: "0",
        padding: "2rem",
        backgroundColor: "Canvas",
        color: "CanvasText",
      });
      reloadButton = document.createElement("button");
      reloadButton.type = "button";
      reloadButton.textContent = "Reload page";
      // This explicit native action keeps other owners' beforeunload guards.
      // If unloading is cancelled, the recovery surface stays owned and visible.
      reloadButton.addEventListener("click", reload);
      dialog.append(reloadButton);
      focusLock();
    },
    release() {
      if (released) return;
      released = true;
      if (activeLock === lock) activeLock = null;
      shieldedEvents.forEach((eventName) =>
        window.removeEventListener(eventName, shield, true),
      );
      window.removeEventListener("focusin", keepFocus, true);
      window.removeEventListener("beforeunload", beforeUnload);
      dialog.removeEventListener("cancel", cancel);
      try {
        if (dialog.open && typeof dialog.close === "function") dialog.close();
      } catch {
        // Removing the top-layer element still ends its modal state.
      } finally {
        dialog.remove();
      }
      if (opener instanceof HTMLElement && opener.isConnected) {
        try {
          opener.focus({ preventScroll: true });
        } catch {
          /* The lock is already released. */
        }
      }
    },
  };
  try {
    activeLock = lock;
    shieldedEvents.forEach((eventName) =>
      window.addEventListener(eventName, shield, {
        capture: true,
        passive: false,
      }),
    );
    window.addEventListener("focusin", keepFocus, true);
    window.addEventListener("beforeunload", beforeUnload);
    dialog.addEventListener("cancel", cancel);
    body.append(dialog);
    dialog.showModal();
    focusLock();
    if (
      !dialog.open ||
      !dialog.isConnected ||
      !dialog.contains(document.activeElement)
    ) {
      lock.release();
      return null;
    }
    return lock;
  } catch {
    lock.release();
    return null;
  }
}
